import type { AnyAction, Store, StoreEnhancer } from "redux";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import type { ThunkAction } from "redux-thunk";
import { withExtraArgument } from "redux-thunk";
import type { ActionType } from "typesafe-actions";

import { ApiClient, createAxiosInstance } from "../services/api";
import type { actions as apiActions } from "./api/actions";
import type { ApiState } from "./api/reducer";
import { api } from "./api/reducer";
import type { JobsState } from "./jobs/reducer";
import { jobs } from "./jobs/reducer";
import type { actions as jupyterImagesActions } from "./jupyterImages/actions";
import type { JupyterImages } from "./jupyterImages/reducer";
import { jupyterImages } from "./jupyterImages/reducer";
import type { UserState } from "./user/reducer";
import { user } from "./user/reducer";

export interface ThunkExtraArgument {
  apiClient: ApiClient;
}

export type Actions = ActionType<
  typeof apiActions | typeof jupyterImagesActions
>;

type ThunkResult<R> = ThunkAction<R, StoreState, ThunkExtraArgument, Actions>;

export type ThunkActionCreator<P = void, R = void> = (
  params: P,
) => ThunkResult<R>;

export interface StoreState {
  api: ApiState;
  jobs: JobsState;
  user: UserState;
  jupyterImages: JupyterImages;
}

export function initStore(): Store<StoreState> {
  const axiosInstance = createAxiosInstance();
  const apiClient = new ApiClient(axiosInstance);

  const enhancers: Array<StoreEnhancer> = [];
  const middlewares = [withExtraArgument({ apiClient })];

  if (
    process.env.NODE_ENV === "development" &&
    window.__REDUX_DEVTOOLS_EXTENSION__
  ) {
    enhancers.push(window.__REDUX_DEVTOOLS_EXTENSION__());
  }

  const store = createStore<StoreState, AnyAction, any, any>(
    combineReducers({
      api,
      jobs,
      user,
      jupyterImages,
    }) as any, //TODO: remove when bug is fixed: https://github.com/reduxjs/redux/issues/2709, and https://github.com/reduxjs/redux/pull/3679
    compose(applyMiddleware(...middlewares), ...enhancers),
  );

  return store;
}
